import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon, Delete as DeleteIcon, Backup as BackupIcon } from '@mui/icons-material';
import { useGetBackupsQuery, useGenerateBackupMutation, useDeleteBackupMutation } from '../../../store/api/backupApi';
import { format } from 'date-fns';

const DatabaseBackup = () => {
  const { data: backups = [], isLoading, refetch } = useGetBackupsQuery();
  const [generateBackup, { isLoading: isGenerating }] = useGenerateBackupMutation();
  const [deleteBackup, { isLoading: isDeleting }] = useDeleteBackupMutation();

  const handleGenerateBackup = async () => {
    try {
      await generateBackup().unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to generate backup:', error);
      alert('Failed to generate backup');
    }
  };

  const handleDelete = async (filename) => {
    if (window.confirm('Are you sure you want to delete this backup?')) {
      try {
        await deleteBackup(filename).unwrap();
        refetch();
      } catch (error) {
        console.error('Failed to delete backup:', error);
        alert('Failed to delete backup');
      }
    }
  };

  const handleDownload = (filename) => {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.29.142:5001/api';
    
    // We can fetch it with authorization and then download via blob
    fetch(`${baseUrl}/backup/download/${filename}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) throw new Error('Download failed');
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    })
    .catch(error => {
      console.error('Failed to download:', error);
      alert('Failed to download backup');
    });
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Database Backups</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
          onClick={handleGenerateBackup}
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating...' : 'Generate Backup'}
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Filename</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No backups found.
                  </TableCell>
                </TableRow>
              ) : (
                backups.map((backup) => (
                  <TableRow key={backup.filename} hover>
                    <TableCell>{backup.filename}</TableCell>
                    <TableCell>
                      {format(new Date(backup.createdAt), 'PPpp')}
                    </TableCell>
                    <TableCell>{formatBytes(backup.size)}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => handleDownload(backup.filename)}>
                        <DownloadIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(backup.filename)} disabled={isDeleting}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DatabaseBackup;
